const BASE = import.meta.env.VITE_API_URL ?? "";

let token: string | null = null; // in memory only — never localStorage (XSS-safe)

export function setToken(t: string | null): void { token = t; }
export const hasToken = (): boolean => token !== null;
/** Returns the raw access token — use only for raw fetch() calls that cannot use api(). */
export const getToken = (): string | null => token;

/** Role from the JWT payload — for UI affordances only; the API re-checks every request. */
export function currentRole(): string | null {
  if (!token) return null;
  try { const p = token.split(".")[1]; return p ? (JSON.parse(atob(p)).role ?? null) : null; }
  catch { return null; }
}

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function refreshAccess(): Promise<boolean> {
  const res = await fetch(`${BASE}/v1/auth/refresh`, { method: "POST", credentials: "include" });
  if (!res.ok) { token = null; return false; }
  token = (await res.json()).token ?? null;
  return token !== null;
}

/** Restore a session on app load from the httpOnly refresh cookie. */
export async function bootstrapSession(): Promise<boolean> { return refreshAccess(); }

export async function logout(): Promise<void> {
  try { await fetch(`${BASE}/v1/auth/logout`, { method: "POST", credentials: "include" }); }
  finally { token = null; }
}

async function raw(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    ...init, credentials: "include",
    headers: { "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}), ...init?.headers },
  });
}

/** Shape the API returns for a failed zod parse. */
interface ZodFlattened {
  formErrors?: string[];
  fieldErrors?: Record<string, string[] | undefined>;
}

/**
 * Turn whatever the server sent into something a person can act on.
 *
 * A failed validation arrives as `{ formErrors, fieldErrors }`, and rendering
 * that object as a flat "Request failed" left people staring at a form with no
 * idea which field was wrong — the server had already said, and the client threw
 * it away.
 */
function errorMessage(error: unknown): string {
  if (typeof error === "string" && error) return error;

  if (error && typeof error === "object") {
    const { formErrors = [], fieldErrors = {} } = error as ZodFlattened;
    const perField = Object.entries(fieldErrors)
      .filter(([, messages]) => messages?.length)
      .map(([field, messages]) => `${field}: ${messages!.join(", ")}`);
    const all = [...formErrors, ...perField];
    if (all.length) return all.join("; ");
  }

  return "Request failed";
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let res = await raw(path, init);
  if (res.status === 401 && await refreshAccess()) res = await raw(path, init); // one silent retry
  if (res.status === 401) { token = null; window.location.href = "/login"; }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, errorMessage(body.error));
  }
  return res.json() as Promise<T>;
}

/**
 * Fetch an authenticated file and hand it to the browser as a download.
 * Raw fetch rather than a plain link: the access token lives in memory, so an
 * `<a href>` would arrive unauthenticated.
 */
export async function downloadFile(path: string, fileName: string): Promise<void> {
  const res = await raw(path);
  if (!res.ok) throw new ApiError(res.status, "Download failed");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export interface Participant {
  id: number; firstName: string; lastName: string; ndisNumber: string | null;
  status: string; email: string | null; phone: string | null;
}
